/* Cơ cấu tổ chức: ghép nhân sự (Sổ lao động) vào bộ phận theo tên. Port từ app cũ. */

export interface OrgUnit { id: string; name: string; nameEn?: string; parent?: string; order?: number | string; headId?: string; extra?: string; note?: string }
export interface OrgEmp { id: string; code?: string; fullName: string; position?: string; positionEn?: string; dept?: string; deptEn?: string; status?: string }
export interface OrgExtra { id?: undefined; fullName: string; position: string; code?: undefined; positionEn?: undefined; status?: undefined }

/** Khóa so khớp tên bộ phận: chuẩn hoá Unicode, bỏ khoảng trắng thừa, không phân biệt hoa thường. */
export const ouKey = (x: unknown): string => String(x || '').normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');

export const ouList = <U extends OrgUnit>(units: U[]): U[] => units.slice().sort((a, b) => (+(a.order ?? 0) || 0) - (+(b.order ?? 0) || 0) || a.name.localeCompare(b.name, 'vi'));

export const ocActive = (e: Pick<OrgEmp, 'status'>): boolean => (e.status || 'working') !== 'left';

const RANK = [/chủ tịch/, /^tổng giám đốc|^ceo/, /phó tổng giám đốc/, /^giám đốc/, /trưởng ban|trưởng phòng|trưởng chi nhánh|trưởng trung tâm|giám đốc (chi nhánh|trung tâm)/, /kế toán trưởng|kế toán phụ trách|phụ trách/, /phó (ban|phòng|giám đốc)/, /trưởng nhóm|tổ trưởng|trưởng bộ phận/];
/** Thứ hạng chức danh (số nhỏ = cấp cao); không nhận ra thì 99. */
export function ocRank(pos: unknown): number {
  const n = ouKey(pos);
  const i = RANK.findIndex(r => r.test(n));
  return i < 0 ? 99 : i;
}

/** Người đang làm việc của bộ phận, xếp theo chức danh rồi mã NV. */
export function ocMembers<E extends OrgEmp>(u: Pick<OrgUnit, 'name'>, employees: E[]): E[] {
  const k = ouKey(u.name);
  return employees.filter(e => ocActive(e) && ouKey(e.dept) === k).sort((a, b) => ocRank(a.position) - ocRank(b.position) || (a.code || '').localeCompare(b.code || ''));
}

/** Trưởng bộ phận: người được chỉ định (còn làm việc), nếu không thì người đứng đầu danh sách khi chức danh đủ cao. */
export function ocHead<E extends OrgEmp>(u: Pick<OrgUnit, 'headId'>, mem: E[], employees: E[]): E | null {
  if (u.headId) { const h = employees.find(e => e.id === u.headId && ocActive(e)); if (h) return h; }
  const c = mem[0];
  return c && ocRank(c.position) <= 6 ? c : null;
}

/** Những người ghi thêm bằng tay (mỗi dòng "Họ tên – Chức danh"), không nằm trong Sổ lao động. */
export function ocExtra(u: Pick<OrgUnit, 'extra'>): { fullName: string; position: string }[] {
  return String(u.extra || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => { const [n, ...t] = l.split(/\s+[–-]\s+/); return { fullName: n.trim(), position: t.join(' – ').trim() }; });
}

/** Mã của bộ phận và mọi bộ phận con cháu. */
export function ocDesc(id: string, units: Pick<OrgUnit, 'id' | 'parent'>[]): Set<string> {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const u of units) if (u.parent && out.has(u.parent) && !out.has(u.id)) { out.add(u.id); grew = true; }
  }
  return out;
}

/** Bộ phận có trong Sổ lao động nhưng chưa khai báo trong cơ cấu, và số người chưa ghi bộ phận. */
export function ocUnmatched(units: Pick<OrgUnit, 'name'>[], employees: OrgEmp[]): { list: { name: string; nameEn: string; n: number }[]; none: number } {
  const keys = new Set(units.map(u => ouKey(u.name)));
  const m = new Map<string, { name: string; nameEn: string; n: number }>();
  let none = 0;
  for (const e of employees.filter(ocActive)) {
    if (!ouKey(e.dept)) { none++; continue; }
    if (!keys.has(ouKey(e.dept))) {
      const x = m.get(ouKey(e.dept)) || { name: (e.dept as string).trim(), nameEn: e.deptEn || '', n: 0 };
      x.n++; m.set(ouKey(e.dept), x);
    }
  }
  return { list: [...m.values()], none };
}
